import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  ContentService,
  type ContentEtags,
  type ContentResource,
  type ContentResourceKey,
} from '@/services/content.service'
import { useLocaleStore } from '@/stores/locale.store'
import { useUIStore } from '@/stores/ui.store'
import type { Locale } from '@/router/locales'

import page from '@/models/page'
import post from '@/models/post'
import attachment from '@/models/attachment'
import taxonomy from '@/models/taxonomy'

import cpt from '@/models/cpt'

type PageModel = ReturnType<typeof page>
type PostModel = ReturnType<typeof post>
type AttachmentModel = ReturnType<typeof attachment>
type TaxonomyModel = ReturnType<typeof taxonomy>

type CptModel = ReturnType<typeof cpt>

export type ContentStoreSnapshot = {
  pages: PageModel[]
  posts: PostModel[]
  attachments: AttachmentModel[]
  taxonomies: TaxonomyModel[]
  membership: CptModel[]
  trainers: CptModel[]
  gyms: CptModel[]
  etags: ContentEtags
  coverage?: ContentCoverage
  routeTarget?: RouteContentTarget | null
}

type ResourceKey = ContentResourceKey

export type RoutableContentResourceKey = 'pages' | 'posts' | 'trainers' | 'gyms'

export type RouteContentTarget = {
  resource: RoutableContentResourceKey
  id: number
}

export type ResourceCoverage =
  | { type: 'none' }
  | { type: 'entities'; ids: number[] }
  | { type: 'full' }

export type ContentCoverage = Partial<Record<ResourceKey, ResourceCoverage>>

type LoadOptions = {
  force?: boolean
  requireFull?: boolean
}

type CacheMeta = {
  fetchedAt: number
  ttlMs: number
  locale: Locale | null
}

const now = () => Date.now()

export const useContentStore = defineStore('content', () => {
  const localeStore = useLocaleStore()
  const ui = useUIStore()
  // Data
  const pages = ref<PageModel[]>([])
  const posts = ref<PostModel[]>([])
  const attachments = ref<AttachmentModel[]>([])
  const taxonomies = ref<TaxonomyModel[]>([])

  const membership = ref<CptModel[]>([])
  const trainers = ref<CptModel[]>([])
  const gyms = ref<CptModel[]>([])
  const faqs = ref<CptModel[]>([])

  // Status
  // isLoading = global/fullscreen preloader
  const isLoading = ref(false)

  // loading[key] = local loaders per resource
  const loading = ref<Partial<Record<ResourceKey, boolean>>>({})

  const errors = ref<Partial<Record<ResourceKey, unknown>>>({})
  const etags = ref<ContentEtags>({})
  const coverage = ref<ContentCoverage>({})
  const routeTarget = ref<RouteContentTarget | null>(null)

  const hasErrors = computed(() => Object.values(errors.value).some((e) => e != null))

  function hasActiveErrors() {
    return Object.values(errors.value).some((e) => e != null)
  }

  // ---- Cache metadata per resource
  const cache = ref<Record<ResourceKey, CacheMeta>>({
    pages: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null },
    posts: { fetchedAt: 0, ttlMs: 5 * 60_000, locale: null },
    attachments: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    taxonomies: { fetchedAt: 0, ttlMs: 60 * 60_000, locale: null },

    membership: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    trainers: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    gyms: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
    faqs: { fetchedAt: 0, ttlMs: 10 * 60_000, locale: null },
  })

  // ---- In-flight promise per resource (hindrer dobbel load)
  const inFlight = new Map<ResourceKey, Promise<void>>()

  function isFresh(key: ResourceKey) {
    const meta = cache.value[key]
    return (
      meta.fetchedAt > 0 &&
      meta.locale === localeStore.currentLocale &&
      now() - meta.fetchedAt < meta.ttlMs
    )
  }

  function markFetched(key: ResourceKey) {
    cache.value[key].fetchedAt = now()
    cache.value[key].locale = localeStore.currentLocale
  }

  function hasLoaded(key: ResourceKey) {
    const meta = cache.value[key]

    return meta.fetchedAt > 0 && meta.locale === localeStore.currentLocale
  }

  function resourceCoverage(key: ResourceKey): ResourceCoverage {
    return coverage.value[key] ?? { type: 'none' }
  }

  function hasFullCoverage(key: ResourceKey) {
    return resourceCoverage(key).type === 'full'
  }

  function dataHasEntity(key: ResourceKey, id: number) {
    switch (key) {
      case 'pages': return pages.value.some((item) => item.id === id)
      case 'posts': return posts.value.some((item) => item.id === id)
      case 'attachments': return attachments.value.some((item) => item.id === id)
      case 'taxonomies': return taxonomies.value.some((item) => item.id === id)
      case 'membership': return membership.value.some((item) => item.id === id)
      case 'trainers': return trainers.value.some((item) => item.id === id)
      case 'gyms': return gyms.value.some((item) => item.id === id)
      case 'faqs': return faqs.value.some((item) => item.id === id)
    }
  }

  function hasEntity(key: ResourceKey, id: number) {
    const currentCoverage = resourceCoverage(key)

    return dataHasEntity(key, id) && (
      currentCoverage.type === 'full'
      || (currentCoverage.type === 'entities' && currentCoverage.ids.includes(id))
    )
  }

  // Generic loader wrapper (per resource)
  async function loadResource(
    key: ResourceKey,
    loader: () => Promise<void>,
    opts?: LoadOptions,
  ) {
    const needsFullResponse = opts?.requireFull && !hasFullCoverage(key)
    if (!opts?.force && !needsFullResponse && isFresh(key)) return

    const existing = inFlight.get(key)
    if (existing) return existing

    if (
      cache.value[key].locale !== null
      && cache.value[key].locale !== localeStore.currentLocale
    ) {
      delete etags.value[key]
      delete coverage.value[key]
    }

    loading.value[key] = true
    errors.value[key] = undefined

    const p = (async () => {
      try {
        await loader()
        markFetched(key)
      } catch (e) {
        errors.value[key] = e
        // Do not update fetchedAt on failure
      } finally {
        loading.value[key] = false
        inFlight.delete(key)
      }
    })()

    inFlight.set(key, p)
    return p
  }

  function applyResource<T>(
    key: ResourceKey,
    result: ContentResource<T>,
    update: (data: T) => void,
  ) {
    if (!result.notModified) {
      update(result.data)
      coverage.value[key] = { type: 'full' }
    }

    if (result.etag === null) {
      delete etags.value[key]
    } else {
      etags.value[key] = result.etag
    }
  }

  function etagForRequest(key: ResourceKey, opts?: LoadOptions) {
    if (opts?.requireFull && !hasFullCoverage(key)) return undefined
    return etags.value[key]
  }

  async function loadSingleResource(key: ResourceKey, opts?: LoadOptions) {
    await loadResource(key, async () => {
      const etag = etagForRequest(key, opts)

      switch (key) {
        case 'pages': {
          const result = await ContentService.pages(etag)
          applyResource(key, result, (data) => {
            pages.value = data
          })
          break
        }
        case 'posts': {
          const result = await ContentService.posts(etag)
          applyResource(key, result, (data) => {
            posts.value = data
          })
          break
        }
        case 'attachments': {
          const result = await ContentService.attachments(etag)
          applyResource(key, result, (data) => {
            attachments.value = data
          })
          break
        }
        case 'taxonomies': {
          const result = await ContentService.taxonomies(etag, taxonomies.value)
          applyResource(key, result, (data) => {
            taxonomies.value = data
          })
          break
        }
        case 'membership': {
          const result = await ContentService.membership(etag)
          applyResource(key, result, (data) => {
            membership.value = data
          })
          break
        }
        case 'trainers': {
          const result = await ContentService.trainers(etag)
          applyResource(key, result, (data) => {
            trainers.value = data
          })
          break
        }
        case 'gyms': {
          const result = await ContentService.gyms(etag)
          applyResource(key, result, (data) => {
            gyms.value = data
          })
          break
        }
        case 'faqs': {
          const result = await ContentService.faqs(etag)
          applyResource(key, result, (data) => {
            faqs.value = data
          })
          break
        }
      }
    }, opts)
  }

  // ---- Loaders (use ContentService)
  async function loadContent(opts?: LoadOptions) {
    // B) control each resource individually for the best cache behavior.
    await Promise.all([
      loadSingleResource('pages', opts),
      loadSingleResource('posts', opts),
      loadSingleResource('attachments', opts),
      loadSingleResource('membership', opts),
      loadSingleResource('trainers', opts),
      loadSingleResource('gyms', opts),
    ])
  }

  async function loadFaqs(opts?: LoadOptions) {
    await loadSingleResource('faqs', opts)
  }

  async function loadTaxonomies(opts?: LoadOptions) {
    await loadSingleResource('taxonomies', opts)
  }

  async function ensureFullResource(key: ResourceKey) {
    await loadSingleResource(key, { requireFull: true })
  }

  async function ensureRouteResource(key: RoutableContentResourceKey) {
    const target = routeTarget.value

    if (target?.resource === key && hasEntity(key, target.id)) return

    if (hasFullCoverage(key)) return

    await ensureFullResource(key)
  }

  /**
   * Call this from layouts / route guards.
   * - default: will NOT refetch if cache is fresh
   * - sets global isLoading for fullscreen preloader
   */
  async function ensureContent(opts?: LoadOptions) {
    isLoading.value = true
    try {
      await Promise.all([
        loadContent(opts),
        loadTaxonomies(opts),
      ])

      if (!hasActiveErrors()) {
        ui.clearBackendUnavailable()
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Manual refresh button / pull-to-refresh (forces)
   */
  async function refreshAll() {
    await ensureContent({ force: true })
  }

  /**
   * Revalidates hydrated SSG data without showing the fullscreen loader.
   * Matching ETags keep the existing reactive arrays untouched.
   */
  async function revalidateContent() {
    await Promise.all([
      loadContent({ force: true }),
      loadTaxonomies({ force: true }),
    ])

    if (!hasActiveErrors()) {
      ui.clearBackendUnavailable()
    }
  }

  /**
   * Promote the route-scoped SSG projection to one complete pages collection.
   * A projected collection ETag is deliberately omitted here because a 304
   * cannot supply the other pages. The returned collection ETag is retained
   * for later revalidation after full coverage exists.
   */
  async function revalidateRouteContent() {
    await ensureFullResource('pages')

    if (!hasActiveErrors()) {
      ui.clearBackendUnavailable()
    }
  }

  function invalidate(keys?: ResourceKey[]) {
    const list = keys ?? (Object.keys(cache.value) as ResourceKey[])
    for (const k of list) {
      cache.value[k].fetchedAt = 0
      cache.value[k].locale = null
      delete etags.value[k]
      delete coverage.value[k]
    }

    routeTarget.value = null
  }

  /**
   * Restores the CMS payload fetched during static generation and marks those
   * resources as current for the active locale.
   */
  function hydrate(snapshot: ContentStoreSnapshot) {
    pages.value = snapshot.pages
    posts.value = snapshot.posts
    attachments.value = snapshot.attachments
    taxonomies.value = snapshot.taxonomies
    membership.value = snapshot.membership
    trainers.value = snapshot.trainers
    gyms.value = snapshot.gyms
    etags.value = snapshot.etags
    coverage.value = snapshot.coverage ?? {
      pages: { type: 'full' },
      posts: { type: 'full' },
      attachments: { type: 'full' },
      taxonomies: { type: 'full' },
      membership: { type: 'full' },
      trainers: { type: 'full' },
      gyms: { type: 'full' },
    }
    routeTarget.value = snapshot.routeTarget ?? null

    const hydratedKeys: ResourceKey[] = [
      'pages',
      'posts',
      'attachments',
      'taxonomies',
      'membership',
      'trainers',
      'gyms',
    ]

    for (const key of hydratedKeys) {
      if (resourceCoverage(key).type === 'none') {
        cache.value[key].fetchedAt = 0
        cache.value[key].locale = null
      } else {
        markFetched(key)
      }
      loading.value[key] = false
      errors.value[key] = undefined
    }
  }

  return {
    // data
    pages,
    posts,
    attachments,
    taxonomies,

    membership, 
    trainers, 
    gyms, 
    faqs,
    
    // status
    isLoading,
    loading,
    errors,
    etags,
    coverage,
    routeTarget,
    hasErrors,

    // cache helpers
    hasLoaded,
    hasEntity,
    hasFullCoverage,
    hydrate,
    ensureContent,
    ensureFullResource,
    ensureRouteResource,
    revalidateContent,
    revalidateRouteContent,
    refreshAll,
    invalidate,

    // direct actions
    loadContent,
    loadFaqs,
    loadTaxonomies,
  }
})
